import React, { useState, useEffect } from 'react';
import {
  Save,
  Building2,
  MapPin,
  Warehouse,
  FolderTree,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { coreAPI } from '../../lib/api';
import type { Company, CompanyProfileDetail } from '../../lib/api/types';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { isPlatformUser } from '../../lib/companyEditAccess';
import { OrgChartNode, NodeType, NODE_COLORS, NODE_TYPE_LABELS } from './types';
import {
  CompanyAddressSection,
  ContactSection,
  LocationSection,
  ParentPicker,
  CompanyAddressValues,
} from './NodeConfigSections';
import { CompanyDetailsSection } from './CompanyDetailsSection';
import { useNodeConfigForm } from './useNodeConfigForm';

interface NodeConfigPanelProps {
  node: OrgChartNode | null;
  parentNode?: OrgChartNode | null;
  companyType?: 'customer' | 'supplier';
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

// Radix Select treats "" as "no value", which would clear the trigger rather
// than register a choice — so the explicit "no parent" option needs a sentinel.
const UNPARENTED = 'none';

const NODE_ICONS: Record<NodeType, React.ElementType> = {
  group: FolderTree,
  division: Building2,
  company: MapPin,
  warehouse: Warehouse,
  location: MapPin,
};

/** Empty string → null so a cleared number field stores as "not specified". */
const toNumberOrNull = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export function NodeConfigPanel({
  node,
  parentNode,
  companyType: defaultCompanyType,
  isOpen,
  onClose,
  onSave,
}: NodeConfigPanelProps) {
  // CR-6247: field state lives in the hook so this file stays under the
  // 500-line limit now that companies carry the expanded field set.
  const { form, setField } = useNodeConfigForm(node);
  // A company's active status is platform-owned; the backend refuses a change
  // from anyone else, so the switch is only offered to platform users.
  const { user } = useAuth();
  const canChangeCompanyStatus = isPlatformUser(user);

  const [saving, setSaving] = useState(false);

  // Both a division and a company hang off a CompanyGroup, so both need a
  // parent. The row-level "Add" actions supply one via `parentNode`; the
  // top-level "Add New" menu does not, so offer a picker there.
  //   division → required, and only a top-level group may parent it
  //   company  → optional, and either a group or a division may parent it
  //              (a division IS a CompanyGroup, so both are valid targets)
  const [parentGroupId, setParentGroupId] = useState<string>('');
  const [availableGroups, setAvailableGroups] = useState<
    Array<{ id: number; name: string; level: number }>
  >([]);

  const isTopLevelCreate = isOpen && node?.entity_id === 0 && !parentNode;
  const needsParentPicker =
    isTopLevelCreate && (node?.type === 'division' || node?.type === 'company');
  const parentIsRequired = node?.type === 'division';

  useEffect(() => {
    if (!needsParentPicker) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await coreAPI.getCompanyGroups();
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results = Array.isArray(raw)
          ? (raw as Array<{ id: number; name: string; level?: number; company_type?: string | null }>)
          : ((raw as { results?: Array<{ id: number; name: string; level?: number; company_type?: string | null }> }).results ?? []);
        setAvailableGroups(
          results
            // Keep the tree on one side: offer groups created for this tab, plus
            // older ones with no recorded side.
            .filter((g) => !defaultCompanyType || !g.company_type || g.company_type === defaultCompanyType)
            // Only a top-level group can parent a division; a company can also
            // sit under a division.
            .filter((g) => (parentIsRequired ? (g.level ?? 0) === 0 : true))
            .map((g) => ({ id: g.id, name: g.name, level: g.level ?? 0 })),
        );
      } catch {
        if (!cancelled) setAvailableGroups([]);
      }
    })();
    return () => { cancelled = true; };
  }, [needsParentPicker, parentIsRequired, defaultCompanyType]);

  // Reset the parent picker whenever a different node opens.
  useEffect(() => {
    setParentGroupId('');
  }, [node]);

  const isCreateMode = node?.entity_id === 0;

  const addressValues: CompanyAddressValues = {
    addressLine1: form.addressLine1,
    addressLine2: form.addressLine2,
    city: form.city,
    stateCounty: form.stateCounty,
    postalCode: form.postalCode,
    country: form.country,
  };

  /** CR-6247: the nested profile payload the company endpoint accepts. */
  const buildProfilePayload = (): CompanyProfileDetail => ({
    employee_count: toNumberOrNull(form.employeeCount),
    year_established: toNumberOrNull(form.yearEstablished),
    annual_revenue: form.annualRevenue.trim() || null,
  });

  /**
   * CR-6247: the currency the form was seeded with — the same resolved value
   * useNodeConfigForm reads. Sending the field back unchanged would overwrite an
   * inheriting site's dormant column, losing the value CR-6256 keeps so that
   * re-selecting Override restores what the site had before. So only send it
   * when the user actually picked something different.
   */
  const seededCurrency =
    node?.effective_settings?.currency?.value || node?.regional_settings?.currency || '';
  const currencyToSend =
    form.currency && form.currency !== seededCurrency ? form.currency : undefined;

  const handleSave = async () => {
    if (!node) return;
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (isCreateMode && node.type === 'company' && !form.primaryEmail.trim()) {
      toast.error('Company email is required');
      return;
    }

    // Without a parent the group would save at level 0 and become a top-level
    // group rather than a division, so require the parent explicitly.
    const divisionParentId = parentNode?.entity_id || (parentGroupId ? Number(parentGroupId) : 0);
    if (isCreateMode && node.type === 'division' && !divisionParentId) {
      toast.error(`Select the parent ${NODE_TYPE_LABELS['group'].toLowerCase()}`);
      return;
    }

    setSaving(true);
    try {
      if (isCreateMode) {
        // CREATE mode — call create APIs
        if (node.type === 'group') {
          await coreAPI.createCompanyGroup({
            name: form.name,
            description: form.description,
            headquarters_country: form.country || undefined,
            website: form.website || undefined,
            parent_group: parentNode?.entity_id || undefined,
            // Record the tab it was created on so an empty group stays there
            // rather than surfacing on every tab.
            company_type: defaultCompanyType,
            is_active: form.isActive,
          });
        } else if (node.type === 'division') {
          // A division IS a CompanyGroup — `level` is derived from parent_group
          // on save, so setting the parent is what makes it a division.
          await coreAPI.createCompanyGroup({
            name: form.name,
            description: form.description,
            headquarters_country: form.country || undefined,
            website: form.website || undefined,
            parent_group: divisionParentId,
            company_type: defaultCompanyType,
            is_active: form.isActive,
          });
        } else if (node.type === 'company') {
          // CR-6691: created active by default (no toggle shown), full address
          await coreAPI.createCompany({
            name: form.name,
            address_line_1: form.addressLine1 || undefined,
            address_line_2: form.addressLine2 || undefined,
            city: form.city || undefined,
            state: form.stateCounty || undefined,
            postal_code: form.postalCode || undefined,
            country: form.country || undefined,
            website: form.website || undefined,
            primary_email: form.primaryEmail || undefined,
            company_type: defaultCompanyType || 'customer',
            // CR-6247: expanded field set saves in the same request.
            registration_number: form.registrationNumber || undefined,
            base_currency: currencyToSend,
            profile: buildProfilePayload(),
            // The row-level action supplies the parent; from the top-level menu
            // it comes from the picker, where "no parent" is allowed.
            group: parentNode?.entity_id
              || (parentGroupId && parentGroupId !== UNPARENTED ? Number(parentGroupId) : undefined),
            is_active: true,
          } as Partial<Company>);
        } else if (node.type === 'warehouse') {
          await coreAPI.createWarehouse({
            name: form.name,
            code: form.code || undefined,
            city: form.city || undefined,
            country: form.country || undefined,
            site: parentNode?.entity_id || undefined,
            is_active: form.isActive,
          });
        } else {
          // No create path for this type — fail loudly rather than reporting a
          // success for a request that was never sent.
          throw new Error(`Cannot create a ${NODE_TYPE_LABELS[node.type]} here`);
        }
        toast.success(`${NODE_TYPE_LABELS[node.type]} created successfully`);
      } else {
        // UPDATE mode — call update APIs
        const entityId = node.entity_id;

        // A division is a nested CompanyGroup, so it updates through the same
        // endpoint. parent_group is deliberately not sent — editing a division
        // must not silently promote it to a top-level group.
        if (node.type === 'group' || node.type === 'division') {
          await coreAPI.updateCompanyGroup(entityId, {
            name: form.name,
            description: form.description,
            headquarters_country: form.country,
            website: form.website,
            is_active: form.isActive,
          });
        } else if (node.type === 'company') {
          await coreAPI.updateCompany(entityId, {
            name: form.name,
            address_line_1: form.addressLine1,
            address_line_2: form.addressLine2,
            city: form.city,
            state: form.stateCounty,
            postal_code: form.postalCode,
            country: form.country,
            website: form.website,
            primary_email: form.primaryEmail,
            // CR-6247: expanded field set. The backend flips the site off
            // group-inherited regional settings only when the currency
            // actually differs from the one in force, so re-saving an
            // unchanged form never detaches it from the group defaults.
            registration_number: form.registrationNumber,
            base_currency: currencyToSend,
            profile: buildProfilePayload(),
            is_active: form.isActive,
          });
        } else if (node.type === 'warehouse') {
          await coreAPI.updateWarehouse(entityId, {
            name: form.name,
            code: form.code,
            city: form.city,
            country: form.country,
            is_active: form.isActive,
          });
        } else {
          throw new Error(`Cannot update a ${NODE_TYPE_LABELS[node.type]} here`);
        }
        toast.success(`${NODE_TYPE_LABELS[node.type]} updated successfully`);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error(`Failed to ${isCreateMode ? 'create' : 'update'}:`, error);
      toast.error(getApiErrorMessage(error, `Failed to ${isCreateMode ? 'create' : 'update'}. Please try again.`));
    } finally {
      setSaving(false);
    }
  };

  if (!node) return null;

  const colors = NODE_COLORS[node.type];
  const Icon = NODE_ICONS[node.type];
  const label = NODE_TYPE_LABELS[node.type];
  const isCompany = node.type === 'company';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', colors.bg)}>
              <Icon className={cn('h-5 w-5', colors.text)} />
            </div>
            <div>
              <Badge className={cn('text-xs text-white mb-1', colors.badge)}>
                {label}
              </Badge>
              <DialogTitle className="text-xl">{isCreateMode ? `New ${label}` : node.name}</DialogTitle>
            </div>
          </div>
          <DialogDescription>
            {isCreateMode
              ? `Create a new ${label.toLowerCase()}${parentNode ? ` under ${parentNode.name}` : ''}`
              : `Edit the details for this ${label.toLowerCase()}`}
          </DialogDescription>
        </DialogHeader>

        {/* Form Content */}
        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Basic Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder={`${label} name`}
                />
              </div>

              {/* No Code field for a site. `createCompany`/`updateCompany`
                  never sent it, so anything typed here was silently dropped on
                  save. `code` itself stays — the warehouse create/update paths
                  below still carry it. */}

              {needsParentPicker && (
                <ParentPicker
                  parentIsRequired={parentIsRequired}
                  parentGroupId={parentGroupId}
                  availableGroups={availableGroups}
                  unparentedValue={UNPARENTED}
                  onChange={setParentGroupId}
                />
              )}

              {(node.type === 'group' || node.type === 'division') && (
                <div className="col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    placeholder="Brief description"
                  />
                </div>
              )}
            </div>
          </div>

          {/* CR-6247: company identity and business details. */}
          {isCompany && (
            <>
              <div className="border-t" />
              <CompanyDetailsSection
                form={form}
                onChange={setField}
                inheritsCurrency={!isCreateMode && node.uses_group_regional_defaults !== false}
              />
            </>
          )}

          <div className="border-t" />

          {/* CR-6691: companies capture a full address; other node types keep
              the simple city/country location block. */}
          {isCompany ? (
            <CompanyAddressSection
              values={addressValues}
              onChange={(field, value) => setField(field, value)}
            />
          ) : (
            <LocationSection
              city={form.city}
              country={form.country}
              onCityChange={(value) => setField('city', value)}
              onCountryChange={(value) => setField('country', value)}
            />
          )}

          {/* Contact Info (for groups and companies) */}
          {(node.type === 'group' || isCompany) && (
            <>
              <div className="border-t" />
              <ContactSection
                showEmail={isCompany}
                primaryEmail={form.primaryEmail}
                website={form.website}
                onEmailChange={(value) => setField('primaryEmail', value)}
                onWebsiteChange={(value) => setField('website', value)}
              />
            </>
          )}

          {/* Status — CR-6691: hidden when creating a company; new companies
              are active by default. Editing a company's status is
              platform-only. */}
          {!(isCreateMode && isCompany) && !(isCompany && !canChangeCompanyStatus) && (
            <>
              <div className="border-t" />
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isActive" className="text-base">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Inactive items are hidden from most views
                  </p>
                </div>
                <Switch
                  id="isActive"
                  checked={form.isActive}
                  onCheckedChange={(value) => setField('isActive', value)}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : isCreateMode ? `Create ${label}` : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NodeConfigPanel;
